import { Fragment, useEffect, useState } from 'react'

import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'

import { DisciplineIcon } from '@components/DisciplineIcon'
import type { Event } from '@models/Event'
import type { Gebied } from '@models/Gebied'
import { formatDate } from '@util/date-util'
import { getEventConfig, questionIdMap } from '@util/event-util'
import { getProjectLabel, updatableProps } from '@util/project-util'

import type { QuickscanQuestion } from '../../aansluiting/project/types/QuickscanQuestion'
import { Label } from '../../theme/components/Label'

interface Props {
  event: Event
  open: boolean
  gebieden: Gebied[]
}

const makeCreateGebiedscodeLabel =
  (gebieden: Gebied[]) => (gebiedscode: string) => {
    const gebied = gebieden.find((g) => g.code === gebiedscode)
    return gebied ? `${gebiedscode} - ${gebied.label}` : gebiedscode
  }

export const EventCard = ({ event, open, gebieden }: Props) => {
  const [localOpen, setLocalOpen] = useState(open)
  const gebiedscodeLabel = makeCreateGebiedscodeLabel(gebieden)

  const { label, Icon, background, expandable, caption } = getEventConfig(event)

  useEffect(() => {
    setLocalOpen(open)
  }, [open])

  return (
    <Card key={event._id} variant="outlined" sx={{ mt: 2 }}>
      <CardHeader
        slotProps={{
          title: {
            variant: 'body1',
          },
          subheader: {
            variant: 'body2',
          },
        }}
        title={label}
        subheader={caption}
        avatar={
          <Avatar sx={{ color: '#333333', background }} alt={label}>
            <Icon />
          </Avatar>
        }
        action={
          expandable && (
            <IconButton>
              {localOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )
        }
        onClick={() => setLocalOpen(!localOpen)}
        sx={{
          cursor: expandable ? 'pointer' : 'default',

          '&:hover .MuiCardHeader-action': {
            background: 'rgba(0, 0, 0, 0.04)',
            borderRadius: '50%',
          },
        }}
      />

      {expandable && (
        <Collapse in={localOpen}>
          <Divider />

          <CardContent>
            {'questions' in event.data && (
              <List sx={{ padding: 0 }}>
                {event.data.questions.map((question, index, arr) => {
                  return (
                    <Fragment key={question.id}>
                      {generateQuickscanCardContent(question)}
                      {index !== arr.length - 1 && (
                        <Divider sx={{ pt: 1, mb: 1 }} />
                      )}
                    </Fragment>
                  )
                })}
              </List>
            )}

            {'netbeheerderDisciplines' in event.data && (
              <Box sx={{ mb: 2, display: 'flex' }}>
                {event.data.netbeheerderDisciplines.map((nbDisc) => (
                  <DisciplineIcon
                    key={nbDisc.disciplineLabel}
                    disciplineCode={nbDisc.disciplineCode}
                    active={true}
                    toolTip={nbDisc.disciplineLabel}
                  />
                ))}
              </Box>
            )}

            {'isHoofdleidingAanleggen' in event.data && ( // hoofdleiding aanleggen excl discipline - via api en verwerkte quickscan
              <List sx={{ padding: 0 }}>
                <ListItem disableGutters sx={{ padding: 0 }}>
                  <ListItemIcon>
                    {event.data.isHoofdleidingAanleggen ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <CancelIcon color="error" />
                    )}
                  </ListItemIcon>
                  <ListItemText>Hoofdleiding aanleggen</ListItemText>
                </ListItem>
              </List>
            )}

            {'hoofdleidingAanleggenDisciplines' in event.data && ( // hoofdleiding aanleggen per discipline
              <List sx={{ padding: 0 }}>
                {event.data.hoofdleidingAanleggenDisciplines &&
                  event.data.hoofdleidingAanleggenDisciplines.map(
                    (d, index, arr) => (
                      <Fragment key={d.discipline}>
                        <ListItem sx={{ padding: 0 }} key={d.discipline}>
                          <ListItemIcon>
                            {d.isHoofdleidingAanleggen ? (
                              <CheckCircleIcon color="success" />
                            ) : (
                              <CancelIcon color="error" />
                            )}
                          </ListItemIcon>

                          <ListItemText
                            primary={`Hoofdleiding aanleggen voor ${d.discipline}`}
                            secondary={'text' in d ? d.text : undefined}
                          />
                        </ListItem>
                        {index !== arr.length - 1 && (
                          <Divider sx={{ pt: 1, mb: 1 }} />
                        )}
                      </Fragment>
                    ),
                  )}
              </List>
            )}

            {event.type === 'update_labels' && (
              <>
                {event.data.labels.length > 0 ? (
                  event.data.labels.map((label) => (
                    <Label key={label} text={label} sx={{ mt: 0 }} />
                  ))
                ) : (
                  <Typography
                    sx={{
                      m: 0,
                    }}
                  >
                    Alle labels verwijderd
                  </Typography>
                )}
              </>
            )}

            {'doorlooptijdWeken' in event.data && (
              <>
                <Typography
                  sx={{
                    m: 0,
                    fontWeight: 700,
                  }}
                >
                  Doorlooptijd
                </Typography>
                <Typography
                  sx={{
                    m: 0,
                  }}
                >
                  {event.data.doorlooptijdWeken} weken
                </Typography>
              </>
            )}

            {'reden' in event.data && (
              <>
                <Typography
                  sx={{
                    m: 0,
                    fontWeight: 700,
                  }}
                >
                  Reden
                </Typography>
                <Typography
                  sx={{
                    m: 0,
                  }}
                >
                  {event.data.reden || 'Geen reden opgegeven.'}
                </Typography>
              </>
            )}

            {'disciplines' in event.data && (
              <List sx={{ padding: 0 }}>
                {event.data.disciplines &&
                  event.data.disciplines.map((d, index, arr) => (
                    <Fragment key={d.discipline}>
                      <ListItem sx={{ padding: 0 }} key={d.discipline}>
                        <ListItemIcon>
                          {d.isCombiUitvoering ? (
                            <CheckCircleIcon color="success" />
                          ) : (
                            <CancelIcon color="error" />
                          )}
                        </ListItemIcon>

                        <ListItemText
                          primary={`${d.discipline} ${
                            d.isCombiUitvoering ? 'in' : 'uit'
                          } combi`}
                          secondary={'reden' in d ? d.reden : undefined}
                        />
                      </ListItem>

                      {index !== arr.length - 1 && (
                        <Divider sx={{ pt: 1, mb: 1 }} />
                      )}
                    </Fragment>
                  ))}
              </List>
            )}

            {event.type === 'hoofdleiding_details' && (
              <List sx={{ padding: 0 }}>
                {Object.entries(event.data).map(([key, value]) => {
                  if (updatableProps.includes(key)) {
                    let displayValue = value

                    if (key === 'aanvraagDatum' && !!displayValue) {
                      displayValue = formatDate(displayValue)
                    }

                    return (
                      <Typography sx={{ m: 0 }} key={key}>
                        <b>{getProjectLabel(key as any)}:</b>{' '}
                        {displayValue?.toString() || '-'}
                      </Typography>
                    )
                  }
                })}
              </List>
            )}

            {event.type === 'gebiedswissel' && (
              <>
                <Typography
                  sx={{
                    m: 0,
                    fontWeight: 700,
                  }}
                >
                  Oud gebied
                </Typography>
                <Typography sx={{ m: 0 }}>
                  {gebiedscodeLabel(event.data.sourceGebiedscode)}
                </Typography>
                <Typography
                  sx={{
                    m: 0,
                    fontWeight: 700,
                  }}
                >
                  Nieuw gebied
                </Typography>
                <Typography sx={{ m: 0 }}>
                  {gebiedscodeLabel(event.data.gebiedscode)}
                </Typography>
              </>
            )}

            {(event.type === 'project_koppelen' ||
              event.type === 'update_project_group') && (
              <>
                <Typography
                  sx={{
                    m: 0,
                    fontWeight: 700,
                  }}
                >
                  Project {event.data.addProject ? 'toegevoegd' : 'verwijderd'}
                </Typography>
                <Typography sx={{ m: 0 }}>
                  {event.data.addProject || event.data.deleteProject}
                </Typography>
                {event.data.linkedProjects && (
                  <>
                    <Typography
                      sx={{
                        m: 0,
                        fontWeight: 700,
                      }}
                    >
                      Nieuwe lijst met projecten
                    </Typography>
                    <Typography sx={{ m: 0 }}>
                      {event.data.linkedProjects.join(', ')}
                    </Typography>
                  </>
                )}
              </>
            )}

            {event.type === 'technisch_gereed' &&
              event.data.disciplineVerzamelingen.map((dv) => (
                <Fragment key={dv.disciplineCode + dv.netbeheerderCode}>
                  <Typography sx={{ fontWeight: '700', mb: 0 }}>
                    {dv.netbeheerderLabel} ({dv.disciplineLabel})
                  </Typography>

                  {dv.technischGereed ? (
                    <Typography>
                      {`Technisch gereed gemeld op ${formatDate(
                        dv.technischGereed,
                      )}`}
                    </Typography>
                  ) : (
                    <Typography>Niet technisch gereed</Typography>
                  )}
                </Fragment>
              ))}
            {event.type === 'product_actief' && (
              <>
                <Typography
                  sx={{
                    m: 0,
                    fontWeight: 700,
                  }}
                >
                  DisciplineID
                </Typography>
                <Typography
                  sx={{
                    m: 0,
                  }}
                >
                  {event.data.disciplineID}
                </Typography>
              </>
            )}
            {event.type === 'hoofdleiding_referentie' && (
              <>
                <Typography
                  sx={{
                    m: 0,
                    fontWeight: 700,
                  }}
                >
                  {event.data.discipline}
                </Typography>
                <Typography
                  sx={{
                    m: 0,
                  }}
                >
                  {event.data.referentie}
                </Typography>
              </>
            )}

            {event.type === 'hoofdleiding_tracelengte' && (
              <>
                <Typography
                  sx={{
                    m: 0,
                    fontWeight: 700,
                  }}
                >
                  {event.data.discipline}
                </Typography>
                <Typography
                  sx={{
                    m: 0,
                  }}
                >
                  {event.data.tracelengte}
                </Typography>
              </>
            )}

            {event.type === 'wijziging_geblokkeerde_weken' && (
              <>
                <Typography
                  sx={{
                    m: 0,
                    fontWeight: 700,
                  }}
                >
                  {event.data.addWeek
                    ? 'Week geblokkeerd'
                    : 'Geblokkeerde week verwijderd'}
                </Typography>
                <Typography sx={{ m: 0 }}>
                  {event.data.addWeek || event.data.deleteWeek}
                </Typography>
              </>
            )}

            {event.type === 'wijziging_bijlage_template' &&
              event.data.documentsoorten &&
              event.data.documentsoorten.length > 0 && (
                <>
                  <Typography
                    sx={{
                      m: 0,
                      fontWeight: 700,
                    }}
                  >
                    Gewijzigde documentsoorten
                  </Typography>
                  <ul>
                    {event.data.documentsoorten.map((ds) => (
                      <li key={ds}>{ds}</li>
                    ))}
                  </ul>
                </>
              )}
            {event.type === 'hoofdleiding_verantwoordelijke' && (
              <>
                <Typography
                  sx={{
                    m: 0,
                    fontWeight: 700,
                  }}
                >
                  Verantwoordelijke
                </Typography>
                <Typography
                  sx={{
                    m: 0,
                  }}
                >
                  {event.data.verantwoordelijke}
                </Typography>
              </>
            )}

            {event.type === 'hoofdleiding_gebiedswissel' && (
              <>
                <Typography
                  sx={{
                    m: 0,
                    fontWeight: 700,
                  }}
                >
                  Oud gebied
                </Typography>
                <Typography sx={{ m: 0 }}>
                  {gebiedscodeLabel(event.data.sourceGebiedscode)}
                </Typography>
                <Typography
                  sx={{
                    m: 0,
                    fontWeight: 700,
                  }}
                >
                  Nieuw gebied
                </Typography>
                <Typography sx={{ m: 0 }}>
                  {gebiedscodeLabel(event.data.gebiedscode)}
                </Typography>
              </>
            )}
            {event.type === 'wijzigen_geschoven_naar_aannemer' &&
              event.data.disciplineVerzamelingen.map((dv) => (
                <Fragment key={dv.disciplineCode + dv.netbeheerderCode}>
                  <Typography sx={{ fontWeight: '700', mb: 0 }}>
                    {dv.netbeheerderLabel} ({dv.disciplineLabel})
                  </Typography>

                  <Typography>
                    {dv.isGeschovenNaarAannemer
                      ? 'Geschoven naar aannemer'
                      : 'Niet geschoven naar aannemer'}
                  </Typography>
                </Fragment>
              ))}

            {'text' in event.data && ( // door deze laatste te houden staat de toelichting altijd onderaan de card
              <>
                <Typography
                  sx={{
                    m: 0,
                    fontWeight: 700,
                  }}
                >
                  Toelichting
                </Typography>
                <Typography
                  sx={{
                    m: 0,
                  }}
                >
                  {event.data.text ||
                    /* v8 ignore next - TODO: missing coverage -- @preserve */ 'Geen toelichting opgegeven.'}
                </Typography>
              </>
            )}
          </CardContent>
        </Collapse>
      )}
    </Card>
  )
}

function generateQuickscanCardContent(question: QuickscanQuestion) {
  const defaultText = () => (
    <ListItem key={question.id} disableGutters sx={{ padding: 0 }}>
      <ListItemIcon>
        {question.answer ? (
          <CheckCircleIcon color="success" />
        ) : (
          <CancelIcon color="error" />
        )}
      </ListItemIcon>

      <ListItemText>{questionIdMap[question.id]}</ListItemText>
    </ListItem>
  )

  switch (question.id) {
    case 'combiuitvoering':
      return question.disciplines
        ? question.disciplines.map((d, index, arr) => (
            <Fragment key={index}>
              <ListItem disableGutters sx={{ padding: 0 }}>
                <ListItemIcon>
                  {d.answer ? (
                    <CheckCircleIcon color="success" />
                  ) : (
                    <CancelIcon color="error" />
                  )}
                </ListItemIcon>

                <ListItemText>
                  {`${d.discipline} ${d.answer ? 'wel' : 'niet'} mee in combi`}

                  {'text' in d && d.text && (
                    <>
                      <br />
                      <Typography variant="caption">{d.text}</Typography>
                    </>
                  )}
                </ListItemText>
              </ListItem>
              {index !== arr.length - 1 && <Divider sx={{ pt: 1, mb: 1 }} />}
            </Fragment>
          ))
        : defaultText()

    case 'hoofdleiding_aanleggen':
      return question.hoofdleidingAanleggenDisciplines
        ? question.hoofdleidingAanleggenDisciplines.map((d, index, arr) => (
            <Fragment key={index}>
              <ListItem sx={{ padding: 0 }} key={index}>
                <ListItemIcon>
                  {d.answer ? (
                    <CheckCircleIcon color="success" />
                  ) : (
                    <CancelIcon color="error" />
                  )}
                </ListItemIcon>

                <ListItemText
                  primary={`Hoofdleiding aanleggen voor ${d.discipline}`}
                  secondary={'text' in d ? d.text : undefined}
                />
              </ListItem>
              {index !== arr.length - 1 && <Divider sx={{ pt: 1, mb: 1 }} />}
            </Fragment>
          ))
        : defaultText()

    default:
      return defaultText()
  }
}
